import {binToHexString, binToPackedString, packedToBin} from "./hex-strings";
import {b64ToBinaryString, base64DecToArr, base64EncArr} from "./base64";
import {ElementFlags, ElementFlagsSize, ElementIdSize, ElementIdWithFlagsSize, KeyFlags} from "../schema/dt-schema";
import {BigEndian} from "./big-endian";

let zeroHash = new Uint8Array(20);
let zeroString = binToPackedString(zeroHash, 0, 20);
let _buf = new Uint8Array(ElementIdSize);
let _buf24 = new Uint8Array(ElementIdWithFlagsSize);

//Holds mappings between local integer IDs (dbId) and database IDs (the long ones)
export class IdMapper {

	constructor(initialData, longKeys = false) {
		this.longKeys = longKeys;
		if (initialData) {
			this.rootId = initialData.rootId;
			this.extIds = initialData.extIds;
			this.extIdToIndex = initialData.extIdToIndex;
			this.numLoaded = initialData.numLoaded;
		} else {
			this.extIds = [zeroString];
			this.extIdToIndex = new Map();
			this.numLoaded = 1;

			//Nasty way to get from a modelId to the corresponding
			//element ID of the row containing the document element
			var buf = longKeys ? _buf24 : _buf;
			buf.fill(0);

			if (longKeys) {
				// V3 row keys carry all four flag bytes, so a reference to the document row arrives as
				// EFDocumentRoot + 20 zero bytes rather than as the bare all-zero buffer created above.
				const keyFlag = ElementFlags.DocumentRoot;
				//Big-Endian flags
				buf[0] = (keyFlag >> 24) & 0xff;
				buf[1] = (keyFlag >> 16) & 0xff;
				buf[2] = (keyFlag >> 8) & 0xff;
				buf[3] = keyFlag & 0xff;
			}

			//rootId should always equal 1, since it's the first call to addEntity
			this.rootId = this.addEntity(binToPackedString(buf, 0, buf.length));
		}

		if (this.rootId !== 1) {
			console.warn("unexpected root ID is not 1");
		}
	}

	isEmpty() {
		// By default, we add the invalid zero id and the root element with dbId = 1.
		return this.numLoaded < 3;
	}

	addEntityFromBuf(buf, offset) {
		var len = this.longKeys ? ElementIdWithFlagsSize : ElementIdSize;

		return this.addEntity(binToPackedString(buf, offset, len));
	}

	addEntityFromB64(extId) {

		let binString = b64ToBinaryString(extId);

		if (binString.length !== 10/*ElementIdSize / 2*/) {
			if (binString.length === 12 /*ElementIdWithFlagsSize/2*/) {
				if(!this.longKeys) {
					//Make sure we use the non-qualified ID for consistency (legacy code path)
					binString = binString.slice(2);
				}
			} else {
				console.warn("unexpected element ID length:", extId);
			}
		}

		return this.addEntity(binString);
	}

	addEntityAndExtractFlagsFromB64(extId) {

		base64DecToArr(extId, _buf24);
		if(!this.longKeys) {
			// drop element flags and get a hash
			extId = binToPackedString(_buf24, 4, ElementIdSize);
		} else {
			extId = binToPackedString(_buf24, 0, ElementIdWithFlagsSize);
		}

		const flags = BigEndian.decodeInt32(_buf24, 0);

		const dbId = this.addEntity(extId)
		return [dbId, flags];
	}

	addEntity(extId, isVirtual) {

		if (!extId) {
			return 0;
		}

		//TODO: Do we need to start using fully qualified IDs in the IdMapper (including the 4 flags bytes)?
		//If yes, this needs to be updated accordingly.
		if (!isVirtual && extId.length !== (this.longKeys ? ElementIdWithFlagsSize/2 : ElementIdSize/2)) {
			console.warn("Unexpected length of element ID", extId.length);
		}

		let dbId = this.extIdToIndex.get(extId);
		if (dbId === undefined) {
			dbId = this.numLoaded++;
			this.extIdToIndex.set(extId, dbId);
			this.extIds[dbId] = extId;
		}

		return dbId;
	}

	addIdAndHash(dbId, extId) {
		this.extIdToIndex.set(extId, dbId);
		this.extIds[dbId] = extId;
		this.numLoaded++;
	}

	getObjectCount() {
		return this.numLoaded;
	}

	getElementId(dbId) {
		return this.extIds[dbId];
	}

	// Extract the key flags embedded in the row key (first 4 bytes / two widechars, big-endian).
	// Only long (v3) keys embed the flags in the element id; for legacy keys the flags are not part
	// of the id, so we report Unknown to let callers fall back to the property database value.
	getElementFlags(dbId) {
		if (!this.longKeys) {
			return ElementFlags.Unknown;
		}

		const eid = this.getElementId(dbId);
		if (!eid) {
			return ElementFlags.Unknown;
		}

		// Each packed char holds two bytes (low byte first, see binToPackedString), so the first
		// two chars carry the 4 big-endian flag bytes: char0 = [b0, b1], char1 = [b2, b3].
		const c0 = eid.charCodeAt(0);
		const c1 = eid.charCodeAt(1);
		return (((c0 & 0xff) << 24) | ((c0 >> 8) << 16) | ((c1 & 0xff) << 8) | (c1 >> 8)) >>> 0;
	}

	getDbIdFromB64ExternalId(extId) {
		const len = base64DecToArr(extId, _buf24);
		let res;
		if(!this.longKeys) {
			if (len === ElementIdSize) {
				// get a hash
				res = binToPackedString(_buf24, 0, ElementIdSize);
			} else if (len === ElementIdWithFlagsSize) {
				// drop element flags and get a hash
				res = binToPackedString(_buf24, 4, ElementIdSize);
			}
		} else {
			if(len == ElementIdWithFlagsSize) {
				res = binToPackedString(_buf24, 0, ElementIdWithFlagsSize);
			}
		}

		if(!res) {
			console.warn("unexpected element ID length:", extId);
		}

		return this.extIdToIndex.get(res);
	}

	getDbIdFromPackedExternalId(packedId) {
		return this.extIdToIndex.get(packedId);
	}

	getEncodedElementIdWithFlags(dbId, flags) {

		let buf = this.getElementIdWithFlags(dbId, flags);

		if (!buf) {
			return null;
		}

		return base64EncArr(buf);
	}

	// Write flags + elId (Bin[] of 24) that corresponds to the given dbId to the buffer.
	getElementIdWithFlags(dbId, flags) {


		let eid = this.getElementId(dbId);
		if (!eid) {
			return null;
		}

		if(this.longKeys) {
			packedToBin(eid, _buf24, 0);
		} else {

			//Convert the element flag to corresponding key flag
			let keyFlag = KeyFlags.Physical;
			if ((flags & ElementFlags.KeyFlagMask) !== 0) {
				keyFlag = KeyFlags.Logical;
			}

			//Big-Endian flags
			BigEndian.encodeInt32(_buf24, 0, keyFlag);

			packedToBin(eid, _buf24, 0 + ElementFlagsSize);
		}

		return _buf24;
	}

	getEncodedElementId(dbId) {

		let eid = this.getElementId(dbId);

		if (!eid) {
			return null;
		}

		const buf = this.longKeys ? _buf24 : _buf;
		packedToBin(eid, buf, 0);

		return base64EncArr(buf);
	}

	getEncodedElementGUID(dbId) {

		let eid = this.getElementId(dbId);

		if (!eid) {
			return null;
		}

		if(this.longKeys) {
			eid = eid.slice(2);
		}

		packedToBin(eid, _buf, 0);

		const hex = binToHexString(_buf).split('');

		// A Revit GUID is formatted in groups of 8-4-4-4-12-8 hexadecimal characters
		const hexGroups = [8,4,4,4,12];

		let dashPosition = 0;
		for (let i = 0; i < hexGroups.length; i++) {
			dashPosition += hexGroups[i];
			hex.splice(dashPosition, 0, '-')
			dashPosition++;
		}

		return hex.join('');
	}

	updateFromList(extIds) {
		const keyLength = this.longKeys ? ElementIdWithFlagsSize / 2 : ElementIdSize / 2;

		for (const extId of extIds) {
			const isVirtual = extId.length !== keyLength;
			this.addEntity(extId, isVirtual);
		}
	}

}
